import { useCallback, useMemo } from 'react';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { captureException, captureWarning } from '~/lib/metrics';
import { QUOTE_API } from '~/lib/constants';
import { QuoteRequirementResponse, QuoteRequirements } from '~/lib/types/basket-types';

export interface RequirementsApiResult {
  data: QuoteRequirements | null;
  error: Error | null;
}

/**
 * Hook to manage API calls to the requirements endpoint.
 * Exposes a method to fetch quote requirements based on service slug, origin, and destination. (fetchRequirements)
 */
export function useRequirementsApi() {
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();

  const fetchRequirements = useCallback(
    async (serviceSlug: string, origin: string, destination: string): Promise<RequirementsApiResult> => {
      if (!serviceSlug || !origin || !destination) {
        return {
          data: null,
          error: new Error('serviceSlug, origin, and destination are required for requirements')
        };
      }

      try {
        const url = `${QUOTE_API}/services/${serviceSlug}/${origin}/${destination}/requirements`;
        const response = await fetchWithTenantAnalytics(tenant, settings, session, url);

        if (response.status === 400) {
          captureWarning('Requirements Invalid', {
            section: 'requirements-api'
          });
          return {
            data: null,
            error: new Error('Requirements invalid - status 400')
          };
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch requirements - invalid http response ${response.status}`);
        }

        const result = (await response.json()) as QuoteRequirementResponse;

        if (!result?.success || !result?.result?.ruleSets) {
          throw new Error(`Failed to fetch requirements - invalid api response: ${result.error?.message}`);
        }

        return {
          data: result.result.ruleSets,
          error: null
        };
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        captureException(err, 'Failed to fetch requirements', {
          section: 'requirements-api'
        });

        return {
          data: null,
          error: err
        };
      }
    },
    [tenant, settings, session]
  );

  return useMemo(
    () => ({
      fetchRequirements
    }),
    [fetchRequirements]
  );
}
